import { skipToken } from "@reduxjs/toolkit/query";
import type { TrainingChapterAssessmentTemplateGrading } from "@repo/app-types/entities/trainingChapterAssessment";
import type { TrainingParticipantChapterGradingId } from "@repo/app-types/entities/trainingParticipantChapterGrading";
import { computeChapterPassRate } from "@repo/app-types/helpers/trainingAssessmentHelpers";
import { menuStyle } from "@repo/ui/Menu/menuStyle";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { selectStyle } from "@repo/ui/Select/style";
import { IconSelector } from "@tabler/icons-react";
import { DateTime } from "luxon";
import { type FC, useMemo } from "react";
import { Button, ListBox, ListBoxItem, Select } from "react-aria-components";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useTrainingParticipantChapterGradingsQuery } from "../../api/entities/trainingParticipant.js";
import { TrainingAssessmentTemplateScoreLabel } from "./trainingAssessmentTemplateScoreLabel.js";

const CURRENT_GRADING_KEY = "_current";

interface ChapterGradingSelectProps {
  projectId: string;
  participantId: string;
  chapterId: string;
  /** `null` means "current grade" (most recent grading). */
  value: TrainingParticipantChapterGradingId | null;
  onChange: (value: TrainingParticipantChapterGradingId | null) => void;
}

interface GradingOption {
  key: string;
  /** `null` represents the "current grade" option. */
  gradingId: TrainingParticipantChapterGradingId | null;
  title: string;
  subtitle: string;
  passRate: number | null;
}

export const ChapterGradingSelect: FC<ChapterGradingSelectProps> = (props) => {
  const { data: gradings } = useTrainingParticipantChapterGradingsQuery({
    projectId: props.projectId,
    participantId: props.participantId,
    chapterId: props.chapterId,
  });

  // All gradings in a chapter use the same playbook; pull the id from any one
  // of them so we can pre-load the assessment tree needed to compute pass rates.
  const playbookId =
    gradings?.[0]?.grading.assessment_template
      ?.recording_assessment_template_id;
  const { data: playbook } = usePlaybookByIdQuery(playbookId ?? skipToken);

  const options = useMemo<readonly GradingOption[]>(() => {
    if (!gradings || gradings.length === 0) {
      return [];
    }

    const [currentGrading, ...previousGradings] = gradings;
    if (!currentGrading) {
      return [];
    }

    const passRateFor = (
      assessmentTemplate: TrainingChapterAssessmentTemplateGrading | null,
    ): number | null => {
      if (!playbook || !assessmentTemplate) {
        return null;
      }
      return computeChapterPassRate(
        playbook.assessment_tree,
        assessmentTemplate.criteria_grading,
      );
    };

    const currentTime = DateTime.fromISO(currentGrading.created_at);

    return [
      {
        key: CURRENT_GRADING_KEY,
        gradingId: null,
        title: "Current grade",
        subtitle: currentTime.toLocaleString(DateTime.TIME_SIMPLE),
        passRate: passRateFor(currentGrading.grading.assessment_template),
      },
      ...previousGradings.map((grading): GradingOption => {
        const time = DateTime.fromISO(grading.created_at);
        return {
          key: grading.id,
          gradingId: grading.id,
          title: time.toLocaleString(DateTime.DATE_MED),
          subtitle: time.toLocaleString(DateTime.TIME_SIMPLE),
          passRate: passRateFor(grading.grading.assessment_template),
        };
      }),
    ];
  }, [gradings, playbook]);

  if (options.length <= 1) {
    return null;
  }

  const selectedKey = props.value ?? CURRENT_GRADING_KEY;
  const selectedOption = options.find((o) => o.key === selectedKey);

  return (
    <Select
      aria-label="Grading"
      value={selectedKey}
      onChange={(key) => {
        const option = options.find((o) => o.key === key);
        if (!option) {
          return;
        }
        props.onChange(option.gradingId);
      }}
    >
      <Button className={selectStyle().button({ className: "shrink-0" })}>
        <div className={selectStyle().value()}>
          {selectedOption?.title ?? "Current grade"}
        </div>
        <IconSelector className={selectStyle().chevron()} />
      </Button>
      <SimpleMenuPopover placement="bottom end">
        <ListBox items={options}>
          {(option) => (
            <ListBoxItem
              id={option.key}
              textValue={`${option.title} ${option.subtitle}`}
              className={menuStyle().item({ className: "py-1.5" })}
            >
              <div className="flex min-w-0 flex-auto flex-col leading-tight">
                <div className="flex items-baseline justify-between gap-8">
                  <span className="truncate text-sm group-selected:font-semibold">
                    {option.title}
                  </span>
                  <TrainingAssessmentTemplateScoreLabel
                    className="text-xs font-normal group-focus:text-white"
                    score={option.passRate}
                  />
                </div>
                <span className="truncate text-xs font-normal text-muted-foreground group-focus:text-white">
                  {option.subtitle}
                </span>
              </div>
            </ListBoxItem>
          )}
        </ListBox>
      </SimpleMenuPopover>
    </Select>
  );
};
